import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { favoritesService } from "../services/favoritesService";
import { trackAddToWishlist } from "../lib/analytics";

export interface FavoriteItem {
  id: string;
  isAuction: boolean;
  likedAt: number; // timestamp ms
}

interface FavoritesContextValue {
  favorites: FavoriteItem[];
  isLiked: (id: string) => boolean;
  toggle: (id: string, isAuction: boolean) => void;
  loading: boolean;
}

const FavoritesContext = createContext<FavoritesContextValue>({
  favorites: [],
  isLiked: () => false,
  toggle: () => {},
  loading: false,
});

const LOCAL_STORAGE_KEY = "fsm_favorites";

function loadLocal(): FavoriteItem[] {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as FavoriteItem[]) : [];
  } catch {
    return [];
  }
}

function saveLocal(items: FavoriteItem[]) {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(items));
}

function clearLocal() {
  localStorage.removeItem(LOCAL_STORAGE_KEY);
}

export function FavoritesProvider({ children }: { children: React.ReactNode }) {
  const { isAuthed, authReady } = useAuth();
  const [favorites, setFavorites] = useState<FavoriteItem[]>([]);
  const [loading, setLoading] = useState(false);
  const syncedRef = useRef(false);

  // Fetch favorites from backend when user logs in
  useEffect(() => {
    if (!authReady) return;

    if (isAuthed && !syncedRef.current) {
      syncedRef.current = true;
      setLoading(true);

      const localFavorites = loadLocal();

      favoritesService
        .getFavorites()
        .then(async (remoteFavorites) => {
          // Merge: push any local-only favorites to backend
          const remoteIds = new Set(remoteFavorites.map((f) => f.id));
          const localOnly = localFavorites.filter((f) => !remoteIds.has(f.id));

          for (const item of localOnly) {
            try {
              const added = await favoritesService.addFavorite(
                item.id,
                item.isAuction,
              );
              remoteFavorites.push(added);
            } catch {
              // If sync fails for one item, continue with the rest
            }
          }

          setFavorites(remoteFavorites);
          saveLocal(remoteFavorites);
        })
        .catch(() => {
          // On failure, keep local favorites
        })
        .finally(() => setLoading(false));
    }

    if (!isAuthed) {
      syncedRef.current = false;
      setFavorites([]);
      clearLocal();
    }
  }, [isAuthed, authReady]);

  const isLiked = useCallback(
    (id: string) => favorites.some((f) => f.id === id),
    [favorites],
  );

  const toggle = useCallback(
    (id: string, isAuction: boolean) => {
      if (!isAuthed) return;

      const alreadyLiked = favorites.some((f) => f.id === id);

      // Optimistic update
      setFavorites((prev) => {
        let next: FavoriteItem[];
        if (alreadyLiked) {
          next = prev.filter((f) => f.id !== id);
        } else {
          next = [...prev, { id, isAuction, likedAt: Date.now() }];
        }
        saveLocal(next);
        return next;
      });

      // add_to_wishlist enkel bij toevoegen. Hier is alleen het shirt-id bekend,
      // dus we pushen een minimaal item (prijs/details ontbreken in favorieten).
      if (!alreadyLiked) trackAddToWishlist({ item_id: id });

      const apiCall = alreadyLiked
        ? favoritesService.removeFavorite(id)
        : favoritesService.addFavorite(id, isAuction);

      apiCall.catch(() => {
        // Rollback on failure
        setFavorites((prev) => {
          let rollback: FavoriteItem[];
          if (alreadyLiked) {
            // Was removed optimistically, add back
            rollback = [...prev, { id, isAuction, likedAt: Date.now() }];
          } else {
            // Was added optimistically, remove
            rollback = prev.filter((f) => f.id !== id);
          }
          saveLocal(rollback);
          return rollback;
        });
      });
    },
    [favorites, isAuthed],
  );

  return (
    <FavoritesContext.Provider value={useMemo(() => ({ favorites, isLiked, toggle, loading }), [favorites, isLiked, toggle, loading])}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  return useContext(FavoritesContext);
}
